// Inline save_memory approval card for the PA chat transcript (DEV-16964).
//
// Ports the refinement chat's MemoryToolCard experience (approve/reject/edit
// on an agent-proposed memory) into PA chat's ToolCallGroup, against the
// unified /v1/memories API. A save_memory call previously rendered as raw
// JSON with no way to act on it — the agent's proposal sat invisible in the
// PENDING queue until an operator happened to find it on /agents/memories.
//
// `save_memory` gets the full approve/reject/edit card. `search_memories`
// renders through the standard tool row like every other read-only tool.
// `update_memory` and `archive_memory` are not (yet) tools PA chat agents
// have access to (apps/persistent-agent-host/src/runtime.ts only wires
// onSearch/onSave into createMemoryTools), so there is nothing to detect for
// those.
//
// The tool call's own result is just `{ id }` (see IMemoryToolsOpts.onSave
// in packages/mastra-layer) — the proposed content/type/priority live in the
// call's ARGS, not the result. The card renders from args immediately, then
// prefers the live row (via useMemoryById) once it loads so the card reflects
// reality even if the memory was already approved/rejected elsewhere (e.g. on
// /agents/memories in another tab) by the time this transcript is viewed.
import type {
  AgentMemoryWithBindings,
  MemoryStatus,
  MemoryType,
  UpdateMemoryInput,
} from '@cortex/types';
import { memoryTypeValues } from '@cortex/types';
import { fork } from '@cortex/utils/fork';
import type { ToolCallView } from '@formant/chat';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';

import { memoryDetailKey, useMemoryById, useUpdateMemory } from '../../../lib/agent-memory';
import { memoryLifecycle, useMemoryApprovalActions } from '../../../lib/memory-approval';
import { useBindableWorkflowNodes } from '../../../lib/workflow-node-binding';
import { MemoryApprovalButtons } from '../../common/MemoryApprovalButtons';
import { MemoryStatusBadge } from '../../common/MemoryStatusBadge';
import { EditUnifiedMemoryModal } from '../../refinement-memory/EditUnifiedMemoryModal';

import './pa-chat-host.css';

interface ISaveMemoryArgs {
  content: string;
  type: MemoryType;
  priority: number;
  /** Agent-proposed scope: org-wide (appliesToAll) vs just the proposing agent. */
  scope: 'agent' | 'org';
}

const memoryTypes: readonly MemoryType[] = memoryTypeValues;

/**
 * Does this tool-result look like a serialized MastraError rather than a
 * save_memory payload? Mastra wraps every thrown tool error as
 * `{ id: '<DOMAIN>_<REASON>', domain, category, details, text, message }`
 * (e.g. `TOOL_EXECUTION_FAILED`) and the host folds tool-error chunks into
 * plain tool-results with that object as `result`. Treating its `id` as a
 * memory id renders an approvable card for a row that was never created —
 * approve then 404s on `/v1/memories/TOOL_EXECUTION_FAILED/approve`.
 */
function isErrorShapedResult(result: unknown): boolean {
  if (result === null || typeof result !== 'object') return false;
  const r = result as Record<string, unknown>;
  return (
    typeof r.domain === 'string' ||
    typeof r.category === 'string' ||
    typeof r.text === 'string' ||
    typeof r.message === 'string'
  );
}

/**
 * Extract the proposed memory's args + its created id from a completed
 * save_memory call. Returns null for anything not cleanly parseable (still
 * running, interrupted before a result, a FAILED call, or an unexpected
 * shape) — the caller falls back to the default JSON row in that case.
 */
export function parseSaveMemoryCall(
  call: ToolCallView,
): { memoryId: string; args: ISaveMemoryArgs } | null {
  if (call.name !== 'save_memory') return null;
  if (!call.hasResult) return null;
  if (call.isError === true) return null;
  const result = call.result;
  if (isErrorShapedResult(result)) return null;
  const rawId =
    result !== null && typeof result === 'object' && 'id' in result
      ? (result as { id: unknown }).id
      : null;
  const memoryId = typeof rawId === 'string' && rawId.length > 0 ? rawId : null;
  if (!memoryId) return null;

  const raw = call.args;
  if (raw === null || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.content !== 'string' || r.content.length === 0) return null;
  const type = memoryTypes.includes(r.type as MemoryType) ? (r.type as MemoryType) : 'CONTEXT';
  const priority = typeof r.priority === 'number' ? r.priority : 0;
  const scope = r.scope === 'org' ? 'org' : 'agent';

  return { memoryId, args: { content: r.content, type, priority, scope } };
}

export function SaveMemoryToolCard({
  memoryId,
  args,
  initialMemory,
}: {
  memoryId: string;
  args: ISaveMemoryArgs;
  /**
   * Prefetched row for `memoryId` from a batched `useMemoriesByIds` call at
   * the transcript level — lets this card skip its own network round trip
   * on first render instead of every card in the transcript firing its own
   * `GET /v1/memories/:id`. Only wired up while the card hasn't gone through
   * an "Edit before approving" yet (see `effectiveId` below).
   */
  initialMemory?: AgentMemoryWithBindings;
}) {
  // "Edit before approving" is copy-on-write server-side: it creates a NEW
  // row and archives this one. The card must follow that new id, or it keeps
  // querying the now-archived original and shows a permanently misleading
  // "Archived." after what the operator experienced as a successful approve.
  const [effectiveId, setEffectiveId] = useState(memoryId);
  const memoryQuery = useMemoryById(effectiveId, {
    initialData: effectiveId === memoryId ? initialMemory : undefined,
  });
  const { approve, reject, error, setError, runMutation } = useMemoryApprovalActions();
  const updateMemory = useUpdateMemory();
  const [editing, setEditing] = useState(false);
  // "Decide later" (Jul 24 standup): the third option beside approve/reject.
  // Purely presentational — the proposal is ALREADY queued (pending/candidate
  // rows live in the review queue); this acknowledges the card and points at
  // the queue instead of forcing an inline decision.
  const [deferred, setDeferred] = useState(false);

  const memory = memoryQuery.data;
  // A fresh agent-authored save always enters PENDING (see createMemory in
  // @cortex/database) — assume that until the live row loads.
  const status: MemoryStatus = memory?.status ?? 'PENDING';
  const content = memory?.content ?? args.content;
  const type = memory?.type ?? args.type;
  const priority = memory?.priority ?? args.priority;
  const orgWide = memory?.appliesToAll ?? args.scope === 'org';
  const nodeBindings = memory?.nodeBindings ?? [];
  const soleNode = nodeBindings.length === 1 ? nodeBindings[0] : undefined;
  const { archived, pending } = memoryLifecycle(status, memory?.archivedAt);

  // Three-way inline scope before approving (DEV-17158): the proposing agent,
  // org-wide, or a specific workflow llm-agent node. The full agent/collection
  // multi-select stays behind "Edit" (the modal) — this is the fast path.
  // `pickingNode` shows the node picker after the operator chooses "workflow
  // node" but before they've picked one (so no PATCH fires on an empty choice).
  const [pickingNode, setPickingNode] = useState(false);
  const scopeSel: 'agent' | 'org' | 'node' = orgWide
    ? 'org'
    : nodeBindings.length > 0 || pickingNode
      ? 'node'
      : 'agent';
  const nodesQuery = useBindableWorkflowNodes({ enabled: pending && scopeSel === 'node' });

  // All scope changes are copy-on-write server-side, so follow the new row id
  // like the edit modal does. An org/agent flip keeps existing bindings (the
  // host's onSave binds the proposing agent); a PATCH only sends what changes.
  const qc = useQueryClient();
  const applyScopePatch = (patch: UpdateMemoryInput) => {
    if (updateMemory.isPending) return;
    fork(
      runMutation(
        updateMemory.mutateAsync({ id: effectiveId, patch }).then((m) => {
          // Prime the successor row's cache BEFORE following it — otherwise the
          // card falls back to the tool-call args while the new id fetches, and
          // the toggle visibly snaps to the pre-flip scope.
          qc.setQueryData(memoryDetailKey(m.id), m);
          setEffectiveId(m.id);
        }),
      ),
    );
  };
  const chooseAgent = () => {
    setPickingNode(false);
    if (!orgWide && nodeBindings.length === 0) return;
    applyScopePatch({ appliesToAll: false, nodeBindings: [] });
  };
  const chooseOrg = () => {
    setPickingNode(false);
    if (orgWide) return;
    applyScopePatch({ appliesToAll: true });
  };
  const chooseNode = () => {
    // Reveal the picker; the PATCH waits until a node is actually selected.
    if (nodeBindings.length === 0) setPickingNode(true);
  };
  const pickNode = (composite: string) => {
    const [workflowId, ...rest] = composite.split('|');
    const nodeKey = rest.join('|');
    if (!workflowId || !nodeKey) return;
    setPickingNode(false);
    applyScopePatch({ appliesToAll: false, nodeBindings: [{ workflowId, nodeKey }] });
  };

  return (
    <div className="pa-chat-memory-card" data-status={status} data-archived={archived || undefined}>
      <div className="pa-chat-memory-card__head">
        <span className="badge badge--info">{type.toLowerCase()}</span>
        {priority === 2 ? <span className="badge badge--warning">pinned</span> : null}
        {priority === 1 ? <span className="badge badge--neutral">important</span> : null}
        {pending ? (
          <span
            className="pa-chat-memory-card__scope"
            role="group"
            aria-label="Memory scope"
            title="Who this memory applies to once approved"
          >
            <button
              type="button"
              className={scopeSel === 'agent' ? 'is-active' : ''}
              disabled={updateMemory.isPending}
              onClick={chooseAgent}
            >
              this agent
            </button>
            <button
              type="button"
              className={scopeSel === 'org' ? 'is-active' : ''}
              disabled={updateMemory.isPending}
              onClick={chooseOrg}
            >
              org-wide
            </button>
            <button
              type="button"
              className={scopeSel === 'node' ? 'is-active' : ''}
              disabled={updateMemory.isPending}
              onClick={chooseNode}
            >
              workflow node
            </button>
          </span>
        ) : orgWide ? (
          <span className="badge badge--neutral">org-wide</span>
        ) : nodeBindings.length > 0 ? (
          <span className="badge badge--neutral">
            {soleNode ? `node: ${soleNode.nodeKey}` : `${nodeBindings.length} nodes`}
          </span>
        ) : null}
        <MemoryStatusBadge status={status} archived={archived} />
        <span className="pa-chat-memory-card__tool-name">save_memory</span>
      </div>

      {pending && scopeSel === 'node' ? (
        <div className="pa-chat-memory-card__node-pick">
          {nodeBindings.length > 1 ? (
            <span className="t-xs t-muted">
              Scoped to {nodeBindings.length} workflow nodes — use Edit to manage them.
            </span>
          ) : (
            <select
              className="input"
              aria-label="Workflow node"
              disabled={updateMemory.isPending || nodesQuery.isLoading}
              value={soleNode ? `${soleNode.workflowId}|${soleNode.nodeKey}` : ''}
              onChange={(e) => pickNode(e.target.value)}
            >
              <option value="" disabled={true}>
                {nodesQuery.isLoading ? 'Loading nodes…' : 'Pick a workflow node…'}
              </option>
              {(nodesQuery.data ?? []).map((n) => (
                <option key={`${n.workflowId}|${n.nodeKey}`} value={`${n.workflowId}|${n.nodeKey}`}>
                  {n.workflowName} › {n.nodeKey}
                </option>
              ))}
            </select>
          )}
        </div>
      ) : null}

      <p className="pa-chat-memory-card__content">{content}</p>

      <footer className="pa-chat-memory-card__footer">
        {pending && deferred ? (
          <span className="t-xs t-muted">
            Saved for later — review it any time in{' '}
            <Link to="/agents/memories" search={{ status: 'pending' }}>
              the memory review queue
            </Link>
            .
          </span>
        ) : pending ? (
          <>
            <MemoryApprovalButtons
              size="xs"
              disabled={approve.isPending || reject.isPending}
              onApprove={() => runMutation(approve.mutateAsync(effectiveId))}
              onReject={() => runMutation(reject.mutateAsync(effectiveId))}
              onEdit={() => setEditing(true)}
            />
            <button
              type="button"
              className="btn btn--xs btn--ghost"
              title="Keep it in the admin review queue without deciding now"
              onClick={() => setDeferred(true)}
            >
              Later
            </button>
          </>
        ) : (
          <span className="t-xs t-muted">
            {archived
              ? 'Archived.'
              : status === 'APPROVED'
                ? 'Approved — live in memory.'
                : 'Rejected.'}
          </span>
        )}
      </footer>

      {error ? <div className="pa-chat-memory-card__error">{error}</div> : null}

      {editing ? (
        <EditUnifiedMemoryModal
          // Remount when the row query resolves: the modal's useState
          // initializers run once, so a modal opened before `memory` loaded
          // would otherwise be stuck with initialScope === undefined —
          // scopeDirty with no user input, scopeValid false, and
          // "Save & approve" permanently disabled with no visible error.
          key={memory ? 'row-loaded' : 'row-loading'}
          memoryId={effectiveId}
          initial={{ content, type, priority, expiresAt: memory?.expiresAt ?? null }}
          // Scope editing needs the live row's binding set — until it loads,
          // the modal hides the scope section (the inline agent/org toggle on
          // the card still covers the coarse flip).
          initialScope={
            memory
              ? {
                  appliesToAll: memory.appliesToAll,
                  agentIds: memory.agentIds,
                  collectionIds: memory.collectionIds,
                  nodeBindings: memory.nodeBindings ?? [],
                  subjectScoped: memory.subjectRef != null,
                }
              : undefined
          }
          skipApprove={!pending}
          onClose={() => setEditing(false)}
          onSaved={(newId) => {
            setEffectiveId(newId);
            setEditing(false);
          }}
          onError={setError}
        />
      ) : null}
    </div>
  );
}
